import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  enableProdMode,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideRouter, Router, RouteReuseStrategy, withComponentInputBinding } from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import * as Sentry from '@sentry/angular';
import { provideTanStackQuery, QueryClient } from '@tanstack/angular-query-experimental';

import {
  authInterceptor,
  NetworkService,
  provideCoreConfig,
  timeoutInterceptor,
  tokenRefreshInterceptor,
  trackingInterceptor,
} from '@movilidad/app-core';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { campusContextInterceptor } from './app/interceptors/campus-context.interceptor';
import { purchaseTimeoutInterceptor } from './app/interceptors/purchase-timeout.interceptor';
import { walletBalanceInterceptor } from './app/interceptors/wallet-balance.interceptor';
import { errorInterceptor } from './app/interceptors/error-interceptor.service';
import { provideBusticketLive } from './app/services/busticket-live.service';
import { provideMovilidadSession } from './app/services/session-hooks';
import { coreConfig } from './environments/core-config';
import { environment } from './environments/environment';
import { BUILD_INFO } from './environments/build-info';

let networkRef: NetworkService | null = null;

if (environment.production) {
  enableProdMode();
  // Auditoría de seguridad 2026-10 (paypal-verbose-console-log-pii, mobile-console-log-dumps-live-child-gps):
  // en producción, console.log/debug/info imprimían órdenes de PayPal con datos del pagador y posiciones
  // GPS en vivo; se leen con adb logcat o el inspector de Safari, y Sentry los guardaba como breadcrumbs.
  // warn y error se quedan (son los que sirven para diagnosticar).
  const noop = () => undefined;
  console.log = noop;
  console.debug = noop;
  console.info = noop;
}

if (environment.sentryDsn) {
  const release = `movilidad-tec@${environment.version}-${BUILD_INFO?.commitHash || 'unknown'}`;
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.production ? 'production' : 'development',
    release,
    integrations: [Sentry.browserTracingIntegration(), Sentry.httpClientIntegration()],
    tracesSampleRate: environment.production ? 0.2 : 1.0,
    tracePropagationTargets: [environment.apiBaseUrl],
    // Auditoría 2026-10 (frontend-sentry-setuser-email-pii): con PII encendido, httpClientIntegration
    // adjuntaba encabezados de las peticiones fallidas (incluido el Bearer) y la IP del usuario.
    sendDefaultPii: false,
    ignoreErrors: [
      // Common browser network noise (Sentry best practice)
      'NetworkError when attempting to fetch resource',
      'The Internet connection appears to be offline',
      'Network request failed',
      'Load failed',
      /^Failed to fetch$/,
      /^Network Error$/,
      /Google Maps API/,
      // Angular HttpClient offline errors (status 0)
      /Http failure response for .+: 0 Unknown Error/,
      // Ruido interno del Hosted Checkout de EVO/Mastercard: su session.js intenta
      // leer el foco del iframe cross-origin en cada click; el pago no se afecta.
      /focusInMainWindow/,
    ],
    beforeSend(event) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const conn = (navigator as any).connection;
      if (conn) {
        event.contexts = {
          ...event.contexts,
          connection: {
            effectiveType: conn.effectiveType,
            downlink: conn.downlink,
            rtt: conn.rtt,
            type: conn.type,
          },
        };
      }

      const online = networkRef?.isOnline() ?? navigator.onLine;
      if (!online) {
        const exceptionValue = event.exception?.values?.[0]?.value ?? '';
        if (/0 Unknown Error|Failed to fetch|NetworkError/i.test(exceptionValue)) {
          return null;
        }
      }
      return event;
    },
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    // Zoneless es el default de Angular 21; se declara explícito para que nadie
    // "arregle" el bootstrap de vuelta a zone.js. La vista sólo se repinta al
    // escribir un signal, en eventos de plantilla, con markForCheck o AsyncPipe
    // (TEC-258): el estado que la plantilla lee vive en signals.
    provideZonelessChangeDetection(),
    // Config de @movilidad/app-core (API, token, storage, Pusher): sale de `environment`.
    provideCoreConfig(coreConfig),
    // Sesión de @movilidad/app-core (TEC-293): rutas de authGuard/guestGuard, llaves que borra el
    // logout y los hooks de Movilidad (push, DriverStore, overlays, navegación, 401 → user:logout).
    ...provideMovilidadSession(),
    // TEC-464: con sesión escucha private-user.{id} y refresca los boletos cuando otro actor los cambia.
    provideBusticketLive(),
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({
      innerHTMLTemplatesEnabled: true,
      useSetInputAPI: true,
    }),
    provideHttpClient(
      withInterceptors([
        trackingInterceptor,
        campusContextInterceptor,
        purchaseTimeoutInterceptor,
        walletBalanceInterceptor,
        timeoutInterceptor,
        authInterceptor,
        tokenRefreshInterceptor,
        errorInterceptor,
      ])
    ),
    provideRouter(routes, withComponentInputBinding()),
    provideTanStackQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            // Disable TanStack retry — the HTTP interceptor already handles retry
            // with exponential backoff for idempotent methods. Having both would
            // multiply attempts (interceptor 3 × TanStack 3 = 9 server requests).
            retry: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
          },
        },
      })
    ),
    ...(environment.sentryDsn
      ? [
          { provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false }) },
          { provide: Sentry.TraceService, deps: [Router] },
          provideAppInitializer(() => {
            inject(Sentry.TraceService);
          }),
        ]
      : []),
  ],
})
  .then((appRef) => {
    networkRef = appRef.injector.get(NetworkService);
  })
  .catch((err) => {
    console.error('Bootstrap failed:', err);
    const root = document.querySelector('app-root');
    if (root) {
      root.innerHTML =
        '<div style="padding:2rem;text-align:center;font-family:sans-serif">' +
        '<p>Error al iniciar. Por favor reinicia la aplicaci\u00f3n.</p>' +
        '<button onclick="location.reload()" style="padding:.5rem 1rem;font-size:1rem">Reintentar</button></div>';
    }
  });
